import type { QuizsetFolderCursorSearchReq } from '@/api/gen/_model/quizsetFolderCursorSearchReq';
import {
  _delete,
  create,
  get,
  listMine,
  searchMine,
} from '@/api/gen/quizset-folder-controller/quizset-folder-controller.gen';
import { ApiTokenStorage } from '@/module/auth/utils/ApiTokenStorage';
import { followEmptyCursorPages } from '@/shared/utils/followEmptyCursorPages';

import type { Folder, FolderSummary } from '../../types/folder.types';
import { toFolder, toFolderFromSearch } from '../folder.adapters';
import {
  reconcileFolderSearchPage,
  rememberCreatedFolder,
  rememberDeletedFolder,
} from '../folderListReconcile';

export interface FolderCursorPage {
  items: Folder[];
  nextCursor?: string;
}

export interface FolderSearchPage {
  items: FolderSummary[];
  nextCursor?: string;
  hasNext: boolean;
  totalCount?: number;
}

export const fetchMyFolders = async (
  cursor?: string,
): Promise<FolderCursorPage> => {
  const res = await listMine({ cursor });
  return {
    items: res.data.items.map(toFolder),
    nextCursor: res.data.nextCursor,
  };
};

export const searchMyFolders = async (
  variables: Omit<QuizsetFolderCursorSearchReq, 'cursor'>,
  cursor?: string,
  signal?: AbortSignal,
): Promise<FolderSearchPage> => {
  // 요청에 실리는 토큰을 보내기 전에 잡는다 — 응답을 기다리는 사이 다른 탭이 계정을 바꿀 수 있다.
  const credential = ApiTokenStorage.get();
  const response = await followEmptyCursorPages(
    pageCursor =>
      searchMine({ ...variables, cursor: pageCursor }, undefined, signal),
    cursor,
  );

  // 검색 인덱스는 원본보다 늦다. 이 세션에서 만들고 지운 폴더와 맞춘다(`folderListCache`).
  return reconcileFolderSearchPage(
    {
      items: response.data.map(toFolderFromSearch),
      nextCursor: response.nextCursor,
      hasNext: response.hasNext,
      totalCount: response.totalCount,
    },
    { variables, isFirstPage: cursor === undefined, credential },
  );
};

/**
 * 폴더 상세. 담긴 퀴즈는 20개씩 내려온다.
 *
 * 첫 조회는 커서 없이 부르고, 이후에는 응답의 `nextCursor` 를 그대로 다시 보낸다.
 */
export const fetchFolderDetail = async (
  id: string,
  cursor?: string,
): Promise<Folder> =>
  toFolder((await get(id, cursor ? { cursor } : undefined)).data);

export const createFolder = async (name: string): Promise<Folder> => {
  const credential = ApiTokenStorage.get();
  const folder = toFolder((await create({ name })).data);
  // 검색 인덱스가 따라올 때까지, 같은 계정의 목록 응답에 끼운다.
  rememberCreatedFolder(folder, credential);
  return folder;
};

/**
 * 폴더 삭제.
 *
 * `deleteItems` 가 `false` 면 담긴 퀴즈를 기본 내 퀴즈로 남기고 폴더만 지우고, `true` 면 담긴 퀴즈까지 지운다.
 */
export const deleteFolder = async (
  id: string,
  deleteItems: boolean,
): Promise<void> => {
  await _delete(id, { deleteItems });
  // 실패하면 여기 오지 않는다 — 일부만 처리됐을 수 있는 폴더를 숨기지 않는다.
  rememberDeletedFolder(id);
};
